import Card from 'components/Display/Cards/Card'
import dayjs from 'dayjs'
import React from 'react'
import {Box, Divider, LinearProgress, Stack, Typography} from '@mui/material'
import meterReadingStatus from 'constants/enum/meterReadingStatus'
import {useGetMeterReadingDailyQuery} from 'store/Store'
import {useTranslation} from 'react-i18next'
import {alpha} from '@mui/material/styles'
import {Colors} from 'appearance'

const NumberReadings = () => {
    const {data: dailyMeterReadings} = useGetMeterReadingDailyQuery()
    const {t} = useTranslation()
    const stats = React.useMemo(() => {
        const initialStats = {
            total: 0,
            success: 0,
            failed: 0,
            latest: null,
        }

        if (!dailyMeterReadings?.length) {
            return {
                total: 0,
                success: 0,
                failed: 0,
                successRate: 0,
                lastUpdated: null,
            }
        }

        const aggregated = dailyMeterReadings.reduce((acc, reading) => {
            acc.total += 1

            switch (reading.status) {
                case meterReadingStatus.PAID:
                    acc.success += 1
                    break
                case meterReadingStatus.FAILED:
                    acc.failed += 1
                    break
                case meterReadingStatus.PENDING:
                default:
                    acc.success += 1
                    break
            }

            const readingDate = reading.date ? dayjs(reading.date) : null
            if (readingDate && (!acc.latest || readingDate.isAfter(acc.latest))) {
                acc.latest = readingDate
            }

            return acc
        }, initialStats)

        const successRate = aggregated.total
            ? Number(((aggregated.success / aggregated.total) * 100).toFixed(1))
            : 0

        return {
            total: aggregated.total,
            success: aggregated.success,
            failed: aggregated.failed,
            successRate,
            lastUpdated: aggregated.latest ? aggregated.latest.format('MMM D, HH:mm') : null,
        }
    }, [dailyMeterReadings])

    const statusSummary = React.useMemo(
        () => [
            {label: 'successful', value: stats.success, color: Colors.success},

            {label: 'failed', value: stats.failed, color: Colors.danger},
        ],
        [stats.failed, stats.success]
    )

    return (
        <Card
            sx={{
                height: '100%',
            }}>
            <Box
                sx={{
                    p: 3,
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 3,
                }}>
                {' '}
                <Typography variant="h6" fontWeight={600} color="text.primary">
                    {t('dashboard.readingsCard.title')}
                </Typography>
                <Box sx={{display: 'flex', flexDirection: 'row', gap: 2, flexWrap: 'wrap'}}>
                    <Box width={'50%'}>
                        <Typography variant="h5" sx={{fontWeight: 600, mt: 1, lineHeight: 1}}>
                            {stats.total}
                        </Typography>
                        <Typography variant="body2" sx={{mt: 0.5}}>
                            {t('dashboard.readingsCard.totalReadings')}
                        </Typography>
                    </Box>

                    <Divider />

                    <Stack
                        direction={{xs: 'column', sm: 'row'}}
                        spacing={2}
                        width={'100%'}
                        sx={{flexWrap: 'wrap'}}>
                        {statusSummary.map((item) => (
                            <Box
                                key={item.label}
                                sx={{
                                    flex: 1,
                                    minWidth: 0,
                                    borderRadius: 2,
                                    p: 2,
                                    border: '1px solid rgba(148, 163, 184, 0.2)',
                                }}>
                                <Typography variant="body2" sx={{mb: 1}}>
                                    {t(`dashboard.readingsCard.${item.label}`)}
                                </Typography>
                                <Stack direction="row" spacing={1} alignItems="baseline">
                                    <Typography
                                        variant="h5"
                                        sx={{fontWeight: 600, color: item.color}}>
                                        {item.value}
                                    </Typography>
                                    <Typography variant="caption" sx={{fontWeight: 500}}>
                                        / {stats.total}
                                    </Typography>
                                </Stack>
                            </Box>
                        ))}
                    </Stack>
                </Box>
                <Box>
                    <Stack direction="row" spacing={1.5} alignItems="center">
                        <Typography variant="body2">
                            {t('dashboard.readingsCard.successRate')}
                        </Typography>
                        <Typography variant="body1" sx={{fontWeight: 600, color: Colors.secondary}}>
                            {stats.successRate}%
                        </Typography>
                    </Stack>
                    <LinearProgress
                        variant="determinate"
                        value={stats.successRate}
                        sx={{
                            mt: 1,
                            height: 8,
                            borderRadius: 999,
                            backgroundColor: (theme) => alpha(theme.palette.secondary.main, 0.2),
                            '& .MuiLinearProgress-bar': {
                                borderRadius: 999,
                                backgroundColor: (theme) => theme.palette.primary.main,
                            },
                        }}
                    />
                </Box>
                {stats.lastUpdated && (
                    <Typography variant="caption" sx={{pt: 1}}>
                        {t('dashboard.readingsCard.lastUpdated')} · {stats.lastUpdated}
                    </Typography>
                )}
            </Box>
        </Card>
    )
}

export default NumberReadings
